// src/renderer/components/editor/ProjectStatusIndicator.tsx

import React from 'react';
import { Check, Zap, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ProjectStatusIndicatorProps {
  /** Project name to display */
  projectName: string;
  /** Whether the project has unsaved changes */
  isDirty: boolean;
  /** Whether a save operation is in progress */
  isSaving: boolean;
  /** Error message if save failed, null/undefined otherwise */
  saveError?: string | null;
  /** Last saved timestamp (ISO string or Date) */
  lastSavedAt?: string | Date | null;
  /** Callback when user clicks to save (when dirty) */
  onSave?: () => void;
}

type StatusState = 'saved' | 'not-saved' | 'saving' | 'error';

function getStatus(
  isDirty: boolean,
  isSaving: boolean,
  saveError?: string | null
): StatusState {
  if (isSaving) return 'saving';
  if (saveError) return 'error';
  if (isDirty) return 'not-saved';
  return 'saved';
}

function formatLastSavedTime(lastSavedAt?: string | Date | null): string {
  if (!lastSavedAt) return 'Never saved';

  const date = typeof lastSavedAt === 'string' ? new Date(lastSavedAt) : lastSavedAt;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) return 'Last saved just now';
  if (diffMinutes < 60) return `Last saved ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `Last saved ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  // Format as date/time for older saves
  return `Last saved ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
}

const STATUS_CONFIG: Record<
  StatusState,
  {
    label: string;
    icon: React.ElementType;
    colorClass: string;
    bgClass: string;
    animate?: boolean;
    ariaLabel: string;
  }
> = {
  saved: {
    label: 'Saved',
    icon: Check,
    colorClass: 'text-green-500',
    bgClass: 'bg-green-500/10',
    ariaLabel: 'All changes saved',
  },
  'not-saved': {
    label: 'Not Saved',
    icon: Zap,
    colorClass: 'text-orange-500',
    bgClass: 'bg-orange-500/10',
    ariaLabel: 'Unsaved changes',
  },
  saving: {
    label: 'Saving...',
    icon: Loader2,
    colorClass: 'text-blue-500',
    bgClass: 'bg-blue-500/10',
    animate: true,
    ariaLabel: 'Saving in progress',
  },
  error: {
    label: 'Save failed',
    icon: AlertCircle,
    colorClass: 'text-red-500',
    bgClass: 'bg-red-500/10',
    ariaLabel: 'Save operation failed',
  },
};

/**
 * Gets a human-readable status description for accessibility
 */
function getStatusLabel(status: StatusState, projectName: string, saveError?: string | null): string {
  const baseLabel = STATUS_CONFIG[status].ariaLabel;
  if (status === 'error' && saveError) {
    return `${projectName}: ${baseLabel}. Error: ${saveError}`;
  }
  return `${projectName}: ${baseLabel}`;
}

export const ProjectStatusIndicator: React.FC<ProjectStatusIndicatorProps> = ({
  projectName,
  isDirty,
  isSaving,
  saveError,
  lastSavedAt,
  onSave,
}) => {
  const status = getStatus(isDirty, isSaving, saveError);
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  const isClickable = status === 'not-saved' && !!onSave;
  const tooltipText = saveError
    ? `Save failed: ${saveError}`
    : formatLastSavedTime(lastSavedAt);
  const accessibilityLabel = getStatusLabel(status, projectName, saveError);

  const handleClick = () => {
    if (isClickable && onSave) {
      onSave();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && isClickable && onSave) {
      e.preventDefault();
      onSave();
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-2 py-1 rounded-md transition-colors',
        isClickable && 'cursor-pointer hover:bg-zinc-800',
        !isClickable && 'cursor-default'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : -1}
      role={isClickable ? 'button' : 'status'}
      aria-label={isClickable ? `Save project ${projectName}` : accessibilityLabel}
      aria-live="polite"
      title={tooltipText}
      data-testid="project-status-indicator"
    >
      {/* Project name */}
      <span className="text-sm text-zinc-400 max-w-[150px] truncate" data-testid="project-name">
        {projectName}
      </span>

      {/* Status indicator */}
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-0.5 rounded',
          config.bgClass
        )}
        data-testid="status-badge"
      >
        <Icon
          className={cn(
            'w-3.5 h-3.5',
            config.colorClass,
            config.animate && 'animate-spin'
          )}
          data-testid="status-icon"
        />
        <span
          className={cn('text-xs font-medium', config.colorClass)}
          data-testid="status-label"
        >
          {config.label}
        </span>
      </div>
    </div>
  );
};

export default ProjectStatusIndicator;
